import { memo, FC, useState, useEffect, useCallback, useMemo } from 'react'

import { useFocusStoreDangerously } from '@please/lrud'
import { scroller } from 'react-scroll'

import { Toggle } from '@/components/Toggle/Toggle'
import { orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { scrollOptions } from '@/constants/scrollers'
import { AdditionalSectionContent } from '@/containers/Usercentrics/AdditionalCategorySectionContent'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { PrivacyCategorySectionProps } from '@/types/componentsPropsTypes'

import {
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
  SectionTitleWrapper,
  ToggleContainer,
  ViewMore,
  ViewMoreContainer,
  ViewMoreIcon,
} from './styles'

const PrivacyCategorySectionRaw: FC<PrivacyCategorySectionProps> = ({
  data,
  tabTitle,
  disabled,
  servicesDecision,
  setServicesDecision,
}) => {
  const updateNode = useFocusStoreDangerously().updateNode
  const [additionalSectionIsOpened, setAdditionalSectionIsOpened] =
    useState(false)

  const focusId = `Toggle_section_${tabTitle}_${data.slug}`
  const mainSectionFocusId = `MainSection_${data.slug}`
  const additionalSectionFocusId = `AdditionalSection_${data.slug}`

  const { defaultFocusChild: sectionDefaultFocusChild, onMove: onSectionMove } =
    useCustomFocus({ focusId })
  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId: mainSectionFocusId,
  })

  useEffect(() => {
    setTimeout(
      () =>
        updateNode(additionalSectionFocusId, {
          disabled: !additionalSectionIsOpened,
        }),
      0,
    )
  }, [disabled])

  const categoryIsChecked = useMemo(() => {
    const categoryServices = data.services.map((service) => service.id)
    const categoryServicesDecision = servicesDecision.filter((service) =>
      categoryServices.includes(service.serviceId),
    )
    return categoryServicesDecision.some((service) => service.status)
  }, [data.services, servicesDecision])

  const onCategoryToogleHandler = useCallback(() => {
    setServicesDecision((prev) => {
      const categoryServices = data.services.map((service) => service.id)
      const servicesDecision = prev.map((service) =>
        categoryServices.includes(service.serviceId)
          ? { serviceId: service.serviceId, status: !categoryIsChecked }
          : service,
      )
      return servicesDecision
    })
  }, [setServicesDecision, categoryIsChecked])

  if (!data) {
    return null
  }
  const scrollHandler = () =>
    scroller.scrollTo(focusId, scrollOptions.UsercentricsScroll)

  return (
    <Section
      orientation={orientation.VERTICAL}
      focusId={focusId}
      onFocused={scrollHandler}
      defaultFocusChild={sectionDefaultFocusChild}
      onMove={onSectionMove}
    >
      <SectionContent
        focusId={mainSectionFocusId}
        orientation={orientation.HORIZONTAL}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
        onFocused={scrollHandler}
      >
        <SectionTitleWrapper>
          <SectionTitle>{data.label}</SectionTitle>
          <SectionDescription>{data.description}</SectionDescription>
        </SectionTitleWrapper>
        <ToggleContainer>
          <Toggle
            disabled={data.isEssential}
            checked={categoryIsChecked}
            onChange={onCategoryToogleHandler}
            id={data.slug}
          />
        </ToggleContainer>
        <ViewMoreContainer>
          {!!data.services?.length && (
            <ViewMore
              onSelected={() => {
                setAdditionalSectionIsOpened((prev) => !prev)
                scrollHandler()
              }}
            >
              <ViewMoreIcon
                src={
                  additionalSectionIsOpened
                    ? icons.controls.chevronUp
                    : icons.controls.chevronDown
                }
                alt=""
              />
            </ViewMore>
          )}
        </ViewMoreContainer>
      </SectionContent>
      {!!data.services?.length && (
        <AdditionalSectionContent
          services={data.services}
          slug={data.slug}
          additionalSectionIsOpened={additionalSectionIsOpened}
          servicesDecision={servicesDecision}
          setServicesDecision={setServicesDecision}
          tabTitle={tabTitle}
        />
      )}
    </Section>
  )
}

export const PrivacyCategorySection = memo(PrivacyCategorySectionRaw)
